/** The tour's "preview" (plan D9): a client-side plan of the draft, in place of a network
 *  preview — there is no tour changeset endpoint (D3), and the Ledger saves only on a
 *  plan of the current body.
 *
 *  - changes: one outcome per change, as the draft holds it;
 *  - conflicts: what the server would refuse, checked here first so Save says why — a
 *    staged file this page no longer holds (a reload keeps the entry, not the file:
 *    re-attach it — a cover, a photo, a route file), coordinates out of range, a malformed URL (an alias's link too), a
 *    maps URL without a meeting point name, a review URL the target requires, an alias
 *    value another alias of the tour already has (exact, trimmed — the server's rule);
 *  - follow_ups: what the write sets off — the guests' itineraries refresh, chat
 *    channels re-sync, access tags reach every booked participant, internal chat turning
 *    off retires its conversation, a fixed-date tour's new start date moves its bookings,
 *    an external ID taken over from its import or handed back to it (these four are
 *    confirmed before saving — `confirmFollowUps`);
 *  - warnings: a save that leaves the tour without departures creates one from its start
 *    time (the funnel does this on any write).
 *
 *  Pure (`localPlan`) plus the editor's `usePreview` hook around it. */
import { useMemo } from 'react';

import type { TourDetail } from '@/api/tours';
import { changeKey, type DraftWireChange } from '@/features/records/draft/changeSet';
import type {
  RecordChangeOutcome,
  RecordChangeset,
  RecordConflict,
  RecordPlannedStep,
  RecordPreview,
  RecordPreviewQuery,
} from '@/features/records/editor/types';
import { isStagedFile, type StagedFiles } from '@/features/records/fields/widgets/stagedFiles';

import { DAY, DAY_KML } from '../itinerary/model';

import { TAKE_OVER_EFFECT, handBackEffect } from '@/features/ext-ids/ownership';

import type { TourBaseline } from './baselines';
import { CHAT_KEYS, CONTEXT_URL_PATHS, TARGET_URL, TOUR_ENTITY } from './catalog';
import { ALIAS_MANAGED } from './collections';
import { sharedFieldWarnings } from './precheck';

const URL_PATHS = new Set(['meeting_point.maps_url', 'review.url', 'photo_sharing.url', 'positive_review_url', ...CONTEXT_URL_PATHS]);

function isHttpUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

export interface LocalPlanContext {
  base: TourBaseline;
  /** The owning operator's name (the shared pre-check fields' warning). */
  operatorName?: string | null;
  detail: Pick<TourDetail, 'departures' | 'user_stats'> & Partial<Pick<TourDetail, 'is_bespoke' | 'internal_chat_disabled_by_operator'>>;
  files: StagedFiles;
}

export function localPlan(changes: readonly DraftWireChange[], ctx: LocalPlanContext): RecordPreview {
  const { base, detail, files } = ctx;
  const rec = base.rec;
  const tour = base.paths[rec] ?? {};
  const touched = new Map<string, unknown>();
  for (const c of changes) if (c.entity === TOUR_ENTITY && c.field) touched.set(c.field, c.to);
  /** The value a path will have after the save. */
  const after = (path: string) => (touched.has(path) ? touched.get(path) : tour[path]);

  const outcomes: RecordChangeOutcome[] = changes.map((c) => ({
    key: changeKey(`${c.entity}:${c.id}`, c.field ?? c.action ?? ''),
    entity: c.entity,
    id: c.id,
    field: c.field ?? null,
    action: c.action ?? null,
    outcome: 'change',
    from: c.from,
    to: c.to,
    verdict: null,
  }));

  const conflicts: RecordConflict[] = [];
  const conflict = (path: string, code: string, detailText: string) =>
    conflicts.push({ code, key: changeKey(rec, path), detail: detailText });

  for (const [path, to] of touched) {
    if (isStagedFile(to) && !files.has(to)) {
      conflict(path, 'file_missing', `Re-attach ${to.name} — a staged file does not survive a reload`);
    }
    if (URL_PATHS.has(path) && text(to) && !isHttpUrl(text(to))) conflict(path, 'invalid_url', 'Enter a valid URL');
  }
  // A day's staged route file is a file like any other.
  for (const c of changes) {
    if (c.entity === DAY && c.field === DAY_KML && isStagedFile(c.to) && !files.has(c.to)) {
      conflicts.push({
        code: 'file_missing',
        key: changeKey(`${c.entity}:${c.id}`, c.field),
        detail: `Re-attach ${c.to.name} — a staged file does not survive a reload`,
      });
    }
  }
  for (const [path, lo, hi] of [
    ['meeting_point.latitude', -90, 90],
    ['meeting_point.longitude', -180, 180],
  ] as const) {
    const v = touched.get(path);
    if (typeof v === 'number' && (v < lo || v > hi)) conflict(path, 'out_of_range', `Between ${lo} and ${hi}`);
  }
  // A maps URL needs a meeting point name — filed on whichever of the two the draft changed.
  if (text(after('meeting_point.maps_url')) && !text(after('meeting_point.name'))) {
    const path = touched.has('meeting_point.name') ? 'meeting_point.name' : 'meeting_point.maps_url';
    if (touched.has(path)) conflict(path, 'required', 'A meeting point name is required with a Google Maps URL');
  }
  // A length set by hand must be a length.
  if (after('length_is_manual') === true && (touched.has('length') || touched.has('length_is_manual'))) {
    const len = after('length');
    if (typeof len !== 'number' || len <= 0) conflict('length', 'required', 'Enter the length — at least a minute');
  }
  if (after('positive_review_target') === TARGET_URL && !text(after('positive_review_url'))) {
    const path = touched.has('positive_review_url') ? 'positive_review_url' : 'positive_review_target';
    if (touched.has(path)) conflict(path, 'required', 'The “URL” target needs a positive review URL');
  }

  // ---- aliases: a link must be a URL; an alias value must not repeat in the list (the
  //      server refuses the later one, positionally — named on the row here first).
  const aliasValue = new Map<number, string>();
  for (const [key, row] of Object.entries(base.paths)) {
    if (!key.startsWith('alias:')) continue;
    aliasValue.set(Number(key.slice(6)), text(row['alias.custom_id']));
  }
  for (const c of changes) {
    if (c.entity !== 'alias' || !c.field) continue;
    const key = changeKey(`alias:${c.id}`, c.field);
    if (c.field === 'alias.custom_id') aliasValue.set(c.id, text(c.to));
    if (c.field === 'alias.ext_link' && text(c.to) && !isHttpUrl(text(c.to))) {
      conflicts.push({ code: 'invalid_url', key, detail: 'Enter a valid URL' });
    }
  }
  const order = touched.get('additional_custom_ids.order') ?? tour['additional_custom_ids.order'];
  const members = Array.isArray(order) ? (order as number[]) : [...aliasValue.keys()];
  for (const c of changes) {
    if (c.entity !== 'alias' || c.field !== 'alias.custom_id' || !members.includes(c.id)) continue;
    const v = text(c.to);
    if (!v) continue;
    if (members.some((id) => id !== c.id && aliasValue.get(id) === v)) {
      conflicts.push({ code: 'duplicate', key: changeKey(`alias:${c.id}`, c.field), detail: `'${v}' is listed twice` });
    }
  }

  const followUps: RecordPlannedStep[] = [];
  if (changes.length > 0) {
    const total = detail.user_stats?.total ?? 0;
    followUps.push({
      code: 'finish_tour_update',
      label: 'Guest itineraries refresh',
      detail: total > 0 ? `${total} ${total === 1 ? 'booking' : 'bookings'} on this tour` : 'no bookings yet',
    });
  }
  if ([...touched.keys()].some((p) => CHAT_KEYS.has(p))) {
    followUps.push({ code: 'chat_resync', label: 'Chat channels re-sync' });
  }
  if (touched.has('atag')) {
    followUps.push({ code: 'atag_fanout', label: 'Access tags reach every booked participant' });
  }
  if (touched.get('disable_internal_chat') === true && tour.disable_internal_chat !== true) {
    followUps.push({
      code: 'internal_chat_off',
      label: 'Internal chat turns off: the guides & staff conversation is retired (history kept)',
      ...(detail.internal_chat_disabled_by_operator ? { detail: 'already off for every tour of this operator' } : {}),
    });
  }
  // Who maintains an alias: a take-over ends its import's updates; a hand-back gives the
  // import the right to overwrite it — and to delete it once its feed stops listing it.
  for (const c of changes) {
    if (c.entity !== 'alias' || c.field !== ALIAS_MANAGED || c.id < 0) continue;
    const stored = base.paths[`alias:${c.id}`] ?? {};
    const value = text(stored['alias.custom_id']) || `#${c.id}`;
    if (c.to === false && stored[ALIAS_MANAGED] === true) {
      followUps.push({ code: 'ext_id_take_over', label: `“${value}” is taken over: ${TAKE_OVER_EFFECT}` });
    } else if (c.to === true && stored[ALIAS_MANAGED] !== true) {
      const integration = stored['alias.integration'];
      const account = stored['alias.restech_account'];
      followUps.push({
        code: 'ext_id_hand_back',
        label: handBackEffect(
          typeof integration === 'string' ? integration : null,
          typeof account === 'string' ? account : null,
          `“${value}”`,
        ),
      });
    }
  }
  const bookings = detail.user_stats?.total ?? 0;
  if (detail.is_bespoke && touched.has('start_date') && bookings > 0) {
    followUps.push({
      code: 'bookings_move',
      label: `${bookings} ${bookings === 1 ? 'booking moves' : 'bookings move'} to ${text(touched.get('start_date')) || 'the new start date'}`,
    });
  }

  const warnings: string[] = [];
  // Pre-check fields are the operator's, shared by its tours (keyed by id).
  for (const path of ['pre_check_settings.lead_booker_fields', 'pre_check_settings.party_member_fields']) {
    if (touched.has(path)) warnings.push(...sharedFieldWarnings(touched.get(path), tour[path], ctx.operatorName));
  }
  const departures = after('departures');
  if (changes.length > 0 && Array.isArray(departures) && departures.length === 0) {
    warnings.push(
      (detail.departures ?? []).length === 0
        ? 'This tour has no departures — saving creates one from its start time.'
        : 'No departure is left — saving creates one from the tour’s start time.',
    );
  }

  return {
    changes: outcomes,
    required_choices: [],
    conflicts,
    effects: {},
    events: [],
    external: [],
    follow_ups: followUps,
    warnings,
  };
}

/** `RecordEditorConfig.usePreview` for the tour: the local plan of the body, at once. */
export function useTourLocalPlan(body: RecordChangeset | null, ctx: LocalPlanContext): RecordPreviewQuery {
  const { base, detail, files, operatorName } = ctx;
  const data = useMemo(
    () => (body ? localPlan(body.changes as DraftWireChange[], { base, detail, files, operatorName: operatorName ?? null }) : undefined),
    [body, base, detail, files, operatorName],
  );
  return { data, isFetching: false, isPlaceholderData: false, error: null };
}
